import { TrendingUp, Wallet, AlertTriangle } from '../../lib/icons';
import { fmt } from '../shared/badges';
import { couleurCout, NEUTRE_DOUX } from '../../lib/couleurs';

// Pourcentages métier : toujours 2 décimales (fmt(…, 2) rend 3 décimales).
const pct = (v) => Number(v ?? 0).toFixed(2).replace('.', ',');

const Bloc = ({ titre, s, accent = 'text-slate-900', testid }) => (
  <div data-testid={testid} className="border border-slate-200 bg-white px-3 py-2">
    <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">{titre}</p>
    <p className={`tabular font-data text-sm font-black ${accent}`}>{fmt(s.olivesKg / 1000, 2)} T · {s.lots} lot(s)</p>
    <p className="tabular font-data text-[11px] text-slate-600">
      Prix moyen {fmt(s.prixMoyenOlives, 3)} TND/kg olives · {fmt(s.prixMoyenHuile, 3)} TND/kg huile
    </p>
    <p className="tabular font-data text-[11px] text-slate-500">
      Huile {fmt(s.huileKg, 1)} kg · {fmt(s.montantTnd, 3)} TND
    </p>
    <p className="font-data text-[11px]">
      {s.rendementFiable ? (
        <span className="text-slate-700">
          Rdt moyen <span className="tabular font-bold">{pct(s.rendementMoyen)} %</span>
        </span>
      ) : (
        <span className="text-amber-800">
          Rdt moyen : en attente ({s.nonTritures} lot(s) non trituré{s.nonTritures > 1 ? 's' : ''})
        </span>
      )}
    </p>
  </div>
);

// Aide à la décision : la référence de négociation est la PROVISION du lot (cours figé).
// La comparaison à la moyenne fournisseur n'est affichée que si elle est fiable, c'est-à-dire
// si le lot est trituré et si la moyenne ne porte que sur des lots au prix déjà fixé et triturés.
export const AideNegociation = ({ stats, lot, prixPropose }) => {
  if (!stats || !lot) return null;

  const provisionKgOlives = lot.poidsNet > 0 ? lot.provisionTnd / lot.poidsNet : 0;
  const brut =
    provisionKgOlives > 0 && prixPropose > 0 ? ((prixPropose - provisionKgOlives) / provisionKgOlives) * 100 : 0;
  // Sous 0,05 %, l'écart est un simple artefact d'arrondi : on l'affiche comme nul.
  const ecartProvision = Math.abs(brut) < 0.05 ? 0 : brut;
  const hausse = ecartProvision > 0;

  // Moyenne fiable : lots au prix fixé, TOUS triturés (rendement non faussé), lot courant trituré.
  const ref = stats.fixes;
  const moyenneFiable = Boolean(lot.trituree) && ref.lots > 0 && ref.rendementFiable && ref.prixMoyenOlives > 0;
  const ecartMoyenne = moyenneFiable ? ((prixPropose - ref.prixMoyenOlives) / ref.prixMoyenOlives) * 100 : null;

  return (
    <div data-testid="aide-negociation" className="border border-slate-300 bg-slate-100 px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 font-heading text-xs font-black uppercase tracking-[0.08em] text-slate-900">
          <TrendingUp size={14} className="text-amber-700" /> Historique fournisseur — campagne {stats.campagne}
        </p>
        <p className="flex items-center gap-1.5 font-data text-[11px] text-slate-600" data-testid="aide-solde">
          <Wallet size={13} />
          Solde dû : <span className="tabular font-bold text-slate-900">{fmt(stats.solde, 3)} TND</span> · Déjà payé {fmt(stats.totalPaye, 3)} · Avance dispo{' '}
          <span className="tabular font-bold">{fmt(stats.avanceDisponible, 3)}</span>
        </p>
      </div>

      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
        <Bloc titre="Total campagne" s={stats.global} testid="aide-global" />
        <Bloc titre="Lots au prix fixé" s={stats.fixes} accent="text-emerald-800" testid="aide-fixes" />
        <Bloc titre="Lots provisoires (à négocier)" s={stats.provisoires} accent="text-amber-800" testid="aide-provisoires" />
      </div>

      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {stats.parQualite.map((q) => (
          <Bloc key={q.id} titre={`Qualité ${q.label}`} s={q} testid={`aide-qualite-${q.id}`} />
        ))}
      </div>

      <div data-testid="aide-ecart" className="mt-2 border border-slate-300 bg-white px-3 py-2">
        <p className="font-data text-xs text-slate-700">
          <span className="font-bold uppercase text-slate-500">Base de négociation (provision au cours figé)</span> :{' '}
          <span className="tabular font-black text-slate-900">{fmt(provisionKgOlives, 3)}</span> TND/kg d’olives ·{' '}
          {fmt(lot.provisionTnd, 3)} TND
        </p>
        <p className="mt-0.5 font-data text-xs text-slate-700">
          Prix proposé : <span className="tabular font-black text-slate-900">{fmt(prixPropose, 3)}</span> TND/kg d’olives ·
          {provisionKgOlives > 0 ? (
            <>
              {' '}écart vs provision :{' '}
              <span
                data-testid="ecart-provision"
                className={`tabular font-black ${couleurCout(ecartProvision, NEUTRE_DOUX)}`}
              >
                {hausse ? '+' : ''}{fmt(ecartProvision, 1)} %
              </span>
            </>
          ) : (
            <span data-testid="ecart-provision" className="font-bold text-amber-800">
              {' '}provision nulle (lot non trituré) — écart indisponible tant que le rendement du lot n’est pas connu
            </span>
          )}
        </p>
        {moyenneFiable ? (
          <p data-testid="ecart-moyenne" className="mt-0.5 font-data text-[11px] text-slate-600">
            Pour information — écart vs prix moyen des lots au prix fixé ({fmt(ref.prixMoyenOlives, 3)} TND/kg olives,{' '}
            {ref.lots} lot(s) trituré(s)) :{' '}
            <span className={`tabular font-bold ${couleurCout(ecartMoyenne, NEUTRE_DOUX)}`}>
              {ecartMoyenne > 0 ? '+' : ''}{fmt(ecartMoyenne, 1)} %
            </span>
          </p>
        ) : (
          <p data-testid="moyenne-desactivee" className="mt-0.5 flex items-start gap-1.5 font-data text-[11px] text-amber-800">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            Comparaison à la moyenne fournisseur désactivée :{' '}
            {!lot.trituree
              ? 'ce lot n’est pas encore trituré (rendement inconnu)'
              : 'aucun lot au prix fixé et trituré ne permet une moyenne fiable'}{' '}
            — seul l’écart vs provision est significatif.
          </p>
        )}
      </div>
    </div>
  );
};
